// The Sun's two looks, switched where they show. Inside 0.02 AU of the Sun
// (4.3 of its radii, where the surface starts to resolve; the same framing in
// artistic scale) a switch under the distance readout picks Realistic, the
// white light the eye would see, or Detailed, the coloured plasma of
// false-colour telescope pictures. It is the same choice as the Sun button in
// the Graphics group, and it goes again on the way out.
import { vt } from '../i18n/viewer.js';

const AU_KM = 149597870.7;
const SUN_RADIUS_KM = 695700;
// Shown inside 0.02 AU and hidden 5% further out, so a camera resting on the
// edge does not make it flicker.
const SHOW_RADII = 0.02 * AU_KM / SUN_RADIUS_KM;
const HIDE_RADII = SHOW_RADII * 1.05;

const CSS = `
.sun-switch { position: fixed; top: 68px; left: 50%; transform: translateX(-50%); z-index: 50; display: inline-flex; align-items: center; gap: 2px;
  min-height: 36px; box-sizing: border-box; padding: 3px; background: var(--sb-bg, #0a0d13); border-radius: 10px;
  font: 500 13px Inter, 'Kohinoor Devanagari', 'Nirmala UI', 'Noto Sans Devanagari', 'PingFang SC', 'Microsoft YaHei', 'Noto Sans SC', system-ui, sans-serif; }
.sun-switch[hidden] { display: none; }
.sun-switch-name { padding: 0 8px 0 9px; color: var(--sb-ink-2, #a8a396); font-weight: 400; }
.sun-switch button { min-height: 30px; padding: 0 12px; background: transparent; border: none; border-radius: 7px; color: var(--sb-ink-2, #a8a396);
  font: inherit; cursor: pointer; transition: background-color .15s ease, color .15s ease; }
.sun-switch button:hover { background: var(--sb-fill-hover, #1b212c); color: var(--sb-ink, #ece7da); }
.sun-switch button[aria-pressed="true"] { background: var(--sb-accent-fill, #15302a); color: var(--sb-accent, #7dd3a8); cursor: default; }
.sun-switch button:focus-visible { outline: 2px solid var(--sb-accent, #7dd3a8); outline-offset: 2px; }
body.fly-mode .sun-switch, body.scale-mode .sun-switch, body.documentary-mode .sun-switch, body.mode-screensaver .sun-switch,
body.rocketry-mode .sun-switch, body.eclipse-mode .sun-switch, body.photo-mode-clean .sun-switch, body.warp-active .sun-switch,
body.game-mode .sun-switch, body.lesson-mode .sun-switch, body.cupola-mode .sun-switch, body.astro-mode .sun-switch { display: none !important; }
@media (max-width: 820px), (pointer: coarse) and (max-height: 600px) { .sun-switch { top: 76px; } }
`;

// ctx: isRealistic(), setRealistic(on), blocked() (another mode owns the
// view, or the camera is in another star system)
export function initSunSwitch(ctx) {
  const style = document.createElement('style');
  style.textContent = CSS;
  document.head.appendChild(style);

  const root = document.createElement('div');
  root.className = 'sun-switch';
  root.setAttribute('role', 'group');
  root.setAttribute('aria-label', vt('sunSwitch.label'));
  root.title = vt('sunSwitch.title');
  root.hidden = true;
  const name = document.createElement('span');
  name.className = 'sun-switch-name';
  name.textContent = vt('sunSwitch.label');
  root.appendChild(name);
  const buttons = [true, false].map((realistic) => {
    const button = document.createElement('button');
    button.type = 'button';
    button.textContent = vt(realistic ? 'sunSwitch.realistic' : 'sunSwitch.detailed');
    button.addEventListener('click', () => {
      if (ctx.isRealistic() !== realistic) ctx.setRealistic(realistic);
    });
    root.appendChild(button);
    return { button, realistic };
  });
  document.body.appendChild(root);

  // After any change of style, from here or the Graphics group.
  function sync() {
    const on = ctx.isRealistic();
    for (const { button, realistic } of buttons) button.setAttribute('aria-pressed', String(realistic === on));
  }
  sync();

  let near = false;
  // Every frame, with the camera's distance from the Sun's centre in its
  // drawn radii.
  function update(radii) {
    near = near ? radii < HIDE_RADII : radii < SHOW_RADII;
    const show = near && !ctx.blocked();
    if (root.hidden === show) root.hidden = !show;
  }

  return { update, sync };
}
